import React, { useEffect } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { LoaderCircleIcon } from 'lucide-react';

import { queryClient } from '@api/queryClient';
import { formatNumberWithComma } from 'utils/format';
import { EventInvitationJobStatus } from 'types/event_invitation_processing.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { inviteGuestsApi } from '@event-editor/api/invite-guests';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useAsyncInviteJobProcessing } from '@event-editor/hooks/eventEditorState';

export const AsyncInviteJobProcessingToast: React.FC = () => {
  const event = useEditableEvent();
  const { showToast, dismissToast } = useToaster();
  const { polling, startPolling, stopPolling } = useAsyncInviteJobProcessing();

  const asyncInviteJobStatusQuery = useQuery({
    queryKey: eventEditorQueryKeys.eventInvitationJobStatus(event.id!),
    queryFn: () => inviteGuestsApi.getLatestInvitationJobStatus({ eventId: event.id! }),
    enabled: !!event.id && polling,
    refetchInterval: 5000, // Poll every 5 seconds
    placeholderData: keepPreviousData,
    staleTime: 0 // Always fetch fresh data when selections change
  });

  // effect to handle polling invitation job status
  useEffect(() => {
    if (!asyncInviteJobStatusQuery.data || asyncInviteJobStatusQuery.isPlaceholderData) {
      return (): void => {
        // do nothing
      };
    }

    const { jobStatus, numInvitees } = asyncInviteJobStatusQuery.data;

    const eventTitle =
      event.title && event.title.length > 50 ? `${event.title.substring(0, 50)}...` : event.title;

    if (
      jobStatus === EventInvitationJobStatus.PROCESSING ||
      jobStatus === EventInvitationJobStatus.UNPROCESSED
    ) {
      showToast(
        <div className="tw-flex tw-items-center">
          <LoaderCircleIcon className="tw-mr-2 tw-animate-spin" />
          <span>
            Inviting {formatNumberWithComma(numInvitees)} guest
            {numInvitees === 0 || numInvitees > 1 ? 's' : ''} to {eventTitle}
            <br />
            You can leave this page as we invite guests. You’ll be notified when the task is
            complete.
          </span>
        </div>,
        {
          keepOpen: true
        }
      );

      // keep polling for job status
      startPolling();
    }

    // show invitations completed toast
    if (jobStatus === EventInvitationJobStatus.PROCESSED) {
      showToast(
        `${formatNumberWithComma(numInvitees)} guest${
          numInvitees === 0 || numInvitees > 1 ? 's' : ''
        } invited for ${eventTitle}`
      );

      // Refetch guests data
      queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });

      // stop polling
      stopPolling();
    }

    // show invitations failed toast
    if (jobStatus === EventInvitationJobStatus.FAILED) {
      showToast('Guests could not be invited. Try again later', {
        variant: ToasterVariant.ERROR
      });

      // stop polling
      stopPolling();
    }

    return (): void => {
      stopPolling();
      dismissToast();
    };
  }, [asyncInviteJobStatusQuery.data]);
  return null;
};
